import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Shield, Plus, Pencil, Trash2, X, Loader2, User } from 'lucide-react';
import { toast } from 'sonner';

const ROLES = ['مطلق', 'عضو إداري', 'محرر'];

const roleClass = (role) => {
  if (role === 'مطلق') return 'bg-amber-100 text-amber-800 border-amber-300';
  if (role === 'عضو إداري') return 'bg-primary/10 text-primary border-primary/30';
  return 'bg-muted text-muted-foreground border-muted';
};

const emptyForm = { reader_number: '', full_name: '', name: '', phone: '', email: '', admin_role: 'عضو إداري' };

const AdminAccountsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRn, setEditingRn] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && user.admin_role !== 'مطلق') { navigate('/admin'); return; }
    load();
  }, [user]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/admin-accounts');
      setAccounts(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل تحميل الحسابات');
    } finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditingRn(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (acc) => {
    setEditingRn(acc.reader_number);
    setForm({
      reader_number: acc.reader_number,
      full_name: acc.full_name || '',
      name: acc.name || '',
      phone: acc.phone || '',
      email: acc.email || '',
      admin_role: acc.admin_role || 'عضو إداري',
    });
    setModalOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      if (editingRn) {
        await api.put(`/admin/admin-accounts/${editingRn}`, {
          full_name: form.full_name,
          name: form.name,
          phone: form.phone,
          email: form.email,
          admin_role: form.admin_role,
        });
        toast.success('تم تحديث الحساب');
      } else {
        await api.post('/admin/admin-accounts', form);
        toast.success('تم إضافة الحساب');
      }
      setModalOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل الحفظ');
    } finally { setSaving(false); }
  };

  const remove = async (acc) => {
    if (!window.confirm(`سيتم حذف الحساب الإداري «${acc.name || acc.full_name}» نهائياً. المتابعة؟`)) return;
    try {
      await api.delete(`/admin/admin-accounts/${acc.reader_number}`);
      toast.success('تم الحذف');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل الحذف');
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-16 h-16 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-accounts-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Shield className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold text-accent">إدارة الحسابات الإدارية</h1>
            <p className="text-muted-foreground text-sm">إضافة، تعديل، حذف، وتبديل أدوار الحسابات الإدارية</p>
          </div>
        </div>
        <Button onClick={openCreate} className="bg-primary hover:bg-primary/90 text-white" data-testid="add-admin-btn">
          <Plus className="w-4 h-4 ml-2" />
          إضافة حساب إداري
        </Button>
      </div>

      <p className="text-muted-foreground">اجمالي: {accounts.length} حساب</p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {accounts.map((acc) => (
          <Card key={acc.reader_number} className="p-5 hover:shadow-md transition-shadow border-2" data-testid={`admin-card-${acc.reader_number}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="font-bold text-accent truncate">{acc.name || acc.full_name}</p>
                  <p className="text-xs text-muted-foreground font-mono">{acc.reader_number}</p>
                </div>
              </div>
              <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold border ${roleClass(acc.admin_role)}`}>{acc.admin_role}</span>
            </div>
            {acc.full_name && acc.full_name !== acc.name && (
              <p className="text-xs text-muted-foreground mb-1 truncate">الاسم الثلاثي: {acc.full_name}</p>
            )}
            {acc.phone && <p className="text-xs text-muted-foreground mb-1" dir="ltr">📞 {acc.phone}</p>}
            {acc.email && <p className="text-xs text-muted-foreground mb-3 truncate" dir="ltr">✉️ {acc.email}</p>}
            <div className="flex gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={() => openEdit(acc)} className="flex-1" data-testid={`edit-${acc.reader_number}`}>
                <Pencil className="w-3.5 h-3.5 ml-1" /> تعديل
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => remove(acc)}
                className="border-red-500 text-red-600 hover:bg-red-50"
                disabled={acc.reader_number === user?.reader_number}
                data-testid={`delete-${acc.reader_number}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setModalOpen(false)}>
          <Card className="p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="admin-form-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-accent">
                {editingRn ? 'تعديل الحساب الإداري' : 'إضافة حساب إداري جديد'}
              </h3>
              <button onClick={() => setModalOpen(false)} className="p-1.5 hover:bg-black/5 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-semibold mb-1">الرقم القرائي</label>
                <Input
                  value={form.reader_number}
                  onChange={(e) => setForm({ ...form, reader_number: e.target.value })}
                  disabled={!!editingRn}
                  data-testid="form-reader-number"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">الاسم الثلاثي</label>
                <Input
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  data-testid="form-full-name"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">الاسم المختصر</label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="اترك فارغاً لاستخدام الاسم الثلاثي"
                  data-testid="form-name"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold mb-1">رقم الهاتف</label>
                  <Input
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    dir="ltr"
                    data-testid="form-phone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-1">البريد الإلكتروني</label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    dir="ltr"
                    data-testid="form-email"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">الدور</label>
                <div className="flex gap-2 flex-wrap">
                  {ROLES.map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setForm({ ...form, admin_role: r })}
                      className={`px-3 py-1.5 rounded-full text-sm border-2 transition-all ${form.admin_role === r ? roleClass(r) + ' font-bold' : 'border-muted text-muted-foreground hover:border-primary/30'}`}
                      data-testid={`role-${r}`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  الصلاحيات تُدار من صفحة «إدارة الصلاحيات» لكل دور.
                </p>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <Button onClick={submit} disabled={saving} className="flex-1 bg-primary hover:bg-primary/90 text-white" data-testid="submit-admin-form">
                {saving ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : null}
                {editingRn ? 'حفظ التعديلات' : 'إضافة الحساب'}
              </Button>
              <Button variant="outline" onClick={() => setModalOpen(false)}>إلغاء</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminAccountsPage;
